import React, { useState } from "react";
import styled from "styled-components";

import Modal from "../modal/Modal";
import ApprovalControl from "../approval-control/ApprovalControl";
import type { DmPendingAmendment } from "../../app/services/dmThreads";
import { describeAmendmentChanges } from "../../utils/contractAmendment";

interface ContractAmendmentReviewModalProps {
  amendment: DmPendingAmendment;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  isProcessing?: boolean;
  errorMessage?: string | null;
}

// 承認 UI はスクロール領域の下端に sticky で固定し、フッターの真上に常に表示する。
// Body の左右 padding を負 margin で打ち消す（OfferReceiveModal と同じ作法）。
const ApprovalRow = styled.div`
  position: sticky;
  bottom: 0;
  margin: 0 -12px;
  padding: 0 12px;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  z-index: 1;
`;

const DiffCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 12px;
  background: #fff;
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
`;

const DiffRow = styled.div`
  display: grid;
  grid-template-columns: 112px 1fr;
  gap: 8px;
  align-items: baseline;
`;

const DiffLabel = styled.div.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

const DiffValue = styled.div.attrs({ className: "bodytext-13" })`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
`;

const Previous = styled.span`
  color: var(--soremo-light-gray);
  text-decoration: line-through;
`;

const Arrow = styled.span`
  color: var(--soremo-light-gray);
`;

const Note = styled.div.attrs({ className: "bodytext-13" })`
  padding: 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: #fff;
`;

const ErrorText = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-green);
`;

/**
 * 契約更新の提案を受け取った受託者が、内容を確認して承認 / 却下するモーダル。
 *
 * 初回オファーの受領（`OfferReceiveModal`）と同じ作法に揃えている:
 * 内容をモーダルで提示し、`ApprovalControl` の**長押し**で承認を確定する。
 * 承認するまで契約は変わらず、却下すれば更新前の契約がそのまま残る。
 *
 * 提案段階では契約書 PDF を生成しないため（承認後に作り直す）、判断材料は
 * 変更前 → 変更後の差分そのものを見せる。
 */
const ContractAmendmentReviewModal: React.FC<
  ContractAmendmentReviewModalProps
> = ({
  amendment,
  onClose,
  onApprove,
  onReject,
  isProcessing = false,
  errorMessage,
}) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const rows = describeAmendmentChanges(amendment.changes);

  return (
    <Modal
      onClose={isProcessing ? undefined : onClose}
      title="契約更新の提案が届きました"
      description="内容を確認して承認すると、この内容で契約が更新されます。"
      size="default"
      primaryAction={{
        label: isProcessing ? "処理中..." : "更新を承認",
        onClick: onApprove,
        disabled: !isConfirmed || isProcessing,
        rightIcon: "send",
      }}
      secondaryAction={{
        label: "却下",
        onClick: onReject,
        disabled: isProcessing,
      }}
    >
      <DiffCard>
        {rows.map((row) => (
          <DiffRow key={row.field}>
            <DiffLabel>{row.label}</DiffLabel>
            <DiffValue>
              <Previous>{row.previous}</Previous>
              <Arrow>→</Arrow>
              <span>{row.next}</span>
            </DiffValue>
          </DiffRow>
        ))}
      </DiffCard>

      <Note>
        承認すると、更新後の内容で契約書を作り直します。更新前の契約書はスレッドに
        残るので、あとから見返せます。却下した場合は更新前の契約がそのまま有効です。
      </Note>

      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

      <ApprovalRow>
        <ApprovalControl
          onConfirm={() => setIsConfirmed(true)}
          onConfirmedChange={setIsConfirmed}
          disabled={isProcessing}
        />
      </ApprovalRow>
    </Modal>
  );
};

export default ContractAmendmentReviewModal;
